import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '~/components/Dialog'
import TableComponent from '~/components/TableComponent'
import { handleErrors } from '~/utils/helpers'
import i18n from '~/components/i18n'
import { RouteTypes } from '~/routes/constants'
import { Button } from 'semantic-ui-react'
import { useNavigate } from 'react-router/dist'
import { CoolFarmCropService } from '~/api/services/coolFarmCrop'

interface CropsDialogProps {
  open: boolean
  onClose: () => void
  footprintId: string | null
}

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('ID'),
    key: 'id'
  },
  {
    name: i18n.t('Name'),
    key: 'name'
  },
  {
    name: i18n.t('Crop Type'),
    key: 'cropTypeLabel'
  },
  {
    name: i18n.t('Step'),
    key: 'step'
  },
  {
    name: i18n.t('Completed'),
    key: 'completed',
    type: 'verified'
  },
  {
    name: i18n.t('Actions')
  }
]

const CropsDialog: React.FC<CropsDialogProps> = (props) => {
  const { open, onClose, footprintId } = props
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [isBusy, setIsBusy] = useState(false)
  const [cropsData, setCropsData] = useState({ rows: [], total: 0, filtered: 0 })
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    search: '',
    column: '',
    sort: ''
  })

  const actions = [
    {
      popupMessage: t('Edit crop'),
      type: 'link',
      name: 'pencil',
      size: 'large',
      link: RouteTypes.COOL_FARM_EDIT_CROP.replace(':id', footprintId),
      replaceKey: ':idCrop'
    }
  ]

  useEffect(() => {
    if (open && footprintId) {
      fetchCrops()
    }
  }, [open, footprintId, dataFiltered])

  async function fetchCrops() {
    if (!footprintId) return

    setIsBusy(true)
    try {
      const response = await CoolFarmCropService.getCrops(footprintId, {
        ...dataFiltered,
        page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
      })
      setCropsData(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  const renderContent = () => {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'end', marginBottom: '1em' }}>
          <Button
            onClick={() => navigate(RouteTypes.COOL_FARM_ANSWER_CROP.replace(':id', footprintId))}
            style={{ color: 'white' }}
          >
            {t('Create new crop')}
          </Button>
        </div>
        <TableComponent
          tableColumnHeaders={tableColumnHeaders}
          dataFiltered={dataFiltered}
          handleChangeDataFiltered={(data, tableColumnHeaders) => {
            setTableColumnHeaders(tableColumnHeaders)
            setDataFiltered(data)
          }}
          rows={cropsData.rows}
          total={cropsData.filtered}
          loading={isBusy}
          removePagination
          animated
          actions={actions}
      />
      </div>
    )
  }

  return (
    <Dialog
      textHeader={t('Crops')}
      textClose={t('Close')}
      style={{ width: 1000, padding: '1em' }}
      noScrollingContent
      isDialogOpen={open}
      handleClose={onClose}
      textContent={renderContent()}
      hideSubmit
    />
  )
}

export default CropsDialog
